import { Chart } from 'chart.js/auto';
import { useEffect } from 'react';
import { formatCurrency as fc } from './StylesHelper';
// Helper function to create and update a stock chart
export const createStockChart = ({
  chartRef,
  chartInstanceRef,
  stockData,
  referencePriceForChart,
  changeAmount,
  setHoveredPrice,
  setHoveredChangeAmount,
  setHoveredChangePercent,
  setHoveredDateTime,
  setIsHovering,
  theme,
}) => {

    // Destroy existing chart instance to avoid memory leaks
  if (chartInstanceRef.current) {
    chartInstanceRef.current.destroy();
  }
  const colorsToUse = theme === 'light' ? { 
    changePos: '#1dcf5b',
    changeNeg: '#ff6347',
  } : {
    changePos: '#00FF00',
    changeNeg: '#FF0000',
  };

  const ctx = chartRef.current.getContext('2d');

  // Prepare labels and data
  const labels = stockData.map((data) => {
    const date = new Date(data.t);
    const options = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  });

  const prices = stockData.map((data) => data.c);

  const referencePrice = referencePriceForChart || (stockData.length > 0 ? stockData[0].c : 0);

  // Plugin to draw horizontal line at reference price
  const horizontalLinePlugin = {
    id: 'horizontalLinePlugin',
    beforeDraw: (chart) => {
      if (chart.tooltip?._active?.length) return;
      const { ctx, chartArea: { right, left }, scales: { y } } = chart;
      ctx.save();
      ctx.strokeStyle = '#707070';
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(left, y.getPixelForValue(referencePrice));
      ctx.lineTo(right, y.getPixelForValue(referencePrice));
      ctx.stroke();
      ctx.restore();
    },
  };

  // Plugin to draw vertical line when hovering
  const verticalLinePlugin = {
    id: 'verticalLinePlugin',
    afterDatasetsDraw: function (chart) {
      if (chart.tooltip && chart.tooltip.getActiveElements().length > 0) {
        const ctx = chart.ctx;
        const activePoint = chart.tooltip.getActiveElements()[0];
        const x = activePoint.element.x;
        const topY = chart.chartArea.top;
        const bottomY = chart.chartArea.bottom;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#707070'; // Same styling as the horizontal line
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.restore();
      }
    },
  };

  // Create a new chart instance
  chartInstanceRef.current = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      spanGaps: true,
      datasets: [{
        data: prices,
        borderColor: changeAmount >= 0.00 ? colorsToUse.changePos : colorsToUse.changeNeg,
        fill: false,
        borderWidth: 2,
        pointRadius: function (context) {
          const index = context.dataIndex;
          const dataset = context.dataset.data;
          return index === dataset.length - 1 ? 2 : 0; // Set radius 5 for the last point, 0 for others
        },
        pointHoverRadius: 5,
        pointBackgroundColor: changeAmount >= 0.00 ? colorsToUse.changePos : colorsToUse.changeNeg,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        y: {
          duration: 0, // Disable animation for y-axis changes
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
      hover: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
      scales: {
        x: {
          display: false,
          grid: { display: false },
          border: { color: '#fff', display: true },
        },
        y: {
          display: false,
          grid: { display: false },
          border: { color: '#fff', display: true },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          mode: 'nearest',
          intersect: false,
          displayColors: false, 
          callbacks: {
            label: function (context) {
              return ''; // This should remove the label text
            },
          },
          external: function (context) {
            const { tooltip } = context;
            if (tooltip.dataPoints) {
              const dataPoint = tooltip.dataPoints[0];
              const index = dataPoint.dataIndex;
              const price = dataPoint.parsed.y;

              // Calculate change amount and percentage from reference price
              const referencePrice = referencePriceForChart || stockData[0]?.c;
              const changeAmount = price - referencePrice;
              const changePercent = (changeAmount / referencePrice) * 100;

              // Update state variables
              setHoveredPrice(price);
              setHoveredChangeAmount(changeAmount);
              setHoveredChangePercent(changePercent);
              setHoveredDateTime(labels[index]);
            }
          },
        },
      },
      font: { family: 'SF Pro Display Medium' },
    },
    plugins: [horizontalLinePlugin, verticalLinePlugin],
  });

  // Add event listeners for mouse enter/leave
  const canvas = chartInstanceRef.current.canvas;
  const handleMouseLeave = () => setIsHovering(false);
  const handleMouseEnter = () => setIsHovering(true);
  canvas.addEventListener('mouseleave', handleMouseLeave);
  canvas.addEventListener('mouseenter', handleMouseEnter);

  // Clean up the event listener and destroy the chart on unmount or re-render
  return () => {
    canvas.removeEventListener('mouseleave', handleMouseLeave);
    canvas.removeEventListener('mouseenter', handleMouseEnter);
    chartInstanceRef.current.destroy();
  };
};